import { useContext } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import { Body, Cell, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconTools from '@/icons/Tools.svg';
import { useActionTypesOrganization } from '@/models/actionType';
import { AgreementFromGetById } from '@/models/agreements';
import { useDatabaseTranslation } from '@/services/i18n';

import { AgreementEditContext } from '../AgreementEditContext';

import {
  getPriceAgreementColumnWidths,
  PriceAgreementTableHeader,
  PriceAgreementTableRow,
  PriceAgreementTableRowEdit,
  PriceAgreementTableRowNew,
} from './PriceAgreementTableRow';

export const ActionsTable = ({
  actions,
  agreement,
}: {
  actions: AgreementFromGetById['prices'];
  agreement: AgreementFromGetById;
}) => {
  const { _compare } = useDatabaseTranslation();
  const { draft } = useContext(AgreementEditContext);

  const withMargin = agreement.clientB;
  const withWorkshopWarrantyDurationInMonths = !agreement.clientB;

  const sorted = actions.sort((a, b) =>
    _compare(a.actionTypeOrganization!.name!, b.actionTypeOrganization!.name!)
  );

  if (draft) {
    return (
      <ActionsTableEdit
        actions={actions}
        withMargin={withMargin}
        withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
      />
    );
  }

  return (
    <Table
      columnWidths={getPriceAgreementColumnWidths({
        withMargin,
        withWorkshopWarrantyDurationInMonths,
      })}
      bordered
    >
      <PriceAgreementTableHeader
        withMargin={withMargin}
        withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
      />
      <Body>
        {sorted.map((price) => (
          <PriceAgreementTableRow
            key={price.id}
            price={price}
            withMargin={withMargin}
            withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
          >
            <ActionNameCell price={price} />
          </PriceAgreementTableRow>
        ))}
        {sorted.length === 0 && (
          <Row>
            <Cell isWholeRow>
              <Paragraph color="secondary">
                <Trans id="settings.organization.entities.agreements.detail.empty.actions">
                  No action prices on this agreement.
                </Trans>
              </Paragraph>
            </Cell>
          </Row>
        )}
      </Body>
    </Table>
  );
};

const ActionsTableEdit = ({
  actions,
  withMargin,
  withWorkshopWarrantyDurationInMonths,
}: {
  actions: AgreementFromGetById['prices'];
  withMargin: boolean;
  withWorkshopWarrantyDurationInMonths: boolean;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { draft } = useContext(AgreementEditContext);

  const { data: { actionTypes = [] } = {} } = useActionTypesOrganization();

  const missingActionTypes = actionTypes.filter(
    (actionType) => !draft.prices.some((price) => price.actionTypeOrganizationId === actionType.id)
  );

  return (
    <Table
      columnWidths={getPriceAgreementColumnWidths({
        withMargin,
        withWorkshopWarrantyDurationInMonths,
        withActions: true,
      })}
      bordered
    >
      <PriceAgreementTableHeader
        withActions
        withMargin={withMargin}
        withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
      />
      <Body>
        {actions.map((price) => (
          <PriceAgreementTableRowEdit
            key={price.id}
            price={price}
            withMargin={withMargin}
            withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
          >
            <ActionNameCell price={price} />
          </PriceAgreementTableRowEdit>
        ))}
        <PriceAgreementTableRowNew
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.table.actions.add',
            message: 'Add action',
          })}
          options={missingActionTypes.map((actionType) => ({
            label: _db(actionType.name),
            ...actionType,
          }))}
          onSelect={(value) => ({
            actionTypeOrganization: value,
            actionTypeOrganizationId: value.id,
          })}
          withMargin={withMargin}
          withWorkshopWarrantyDurationInMonths={withWorkshopWarrantyDurationInMonths}
        />
      </Body>
    </Table>
  );
};

const ActionNameCell = ({ price }: { price: AgreementFromGetById['prices'][number] }) => {
  const { _db } = useDatabaseTranslation();

  return (
    <Stack row gap="0.35rem" alignItems="center" flexWrap="nowrap">
      <IconTools style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
      <Paragraph>{_db(price.actionTypeOrganization!.name!)}</Paragraph>
    </Stack>
  );
};
